import { useState, useEffect } from 'react';
import { IconPlus, IconMinus, IconCheck, IconX } from '@tabler/icons-react';
import { Modal, Button } from './ui';
import ProductImage from './ProductImage';

/**
 * ProductDetailsModal — opens when a cashier taps a product card.
 * Layout: full-bleed 16:9 image header at the top (placeholder fills
 * the same shape so non-photo products don't look broken), then a
 * stacked body of size / ingredients / addons / note / qty + add CTA.
 */

export interface ProductSize {
  name: string;
  priceDelta: number;
}
export interface ProductAddon {
  name: string;
  priceCents: number;
}
export interface ProductDetails {
  id: string;
  name: string;
  description?: string;
  category: string;
  basePriceCents: number;
  imageUrl?: string;
  bgClassName?: string;
  sizes?: ProductSize[];
  defaultIngredients?: string[];
  availableAddons?: ProductAddon[];
}

export interface ProductCustomisation {
  size: string;
  unitPriceCents: number;
  excludedIngredients: string[];
  addedAddons: ProductAddon[];
  note: string;
  qty: number;
}

interface Props {
  open: boolean;
  product: ProductDetails | null;
  /** When set, the modal pre-populates from this customisation and the
   *  CTA reads "Update" instead of "Add" — used for re-editing an
   *  existing cart line. */
  editing?: ProductCustomisation | null;
  onClose: () => void;
  onAdd: (customisation: ProductCustomisation) => void;
}

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

export default function ProductDetailsModal({ open, product, editing, onClose, onAdd }: Props) {
  const [size, setSize] = useState<string>('');
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [addons, setAddons] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [qty, setQty] = useState(1);

  const isEditing = editing != null;

  // Reset state ONLY when the modal opens or the *target* product / mode
  // actually changes. The parent re-derives its catalogue array on every
  // render, so passing `product` or `editing` as deps would re-fire this
  // effect on unrelated re-renders (cart updates, scroll-induced re-
  // paints, etc.) and wipe the user's customisations mid-selection.
  // Keying on the stable product id + the editing flag fixes it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open || !product) return;
    if (editing) {
      setSize(editing.size);
      setExcluded(new Set(editing.excludedIngredients));
      setAddons(new Set(editing.addedAddons.map((a) => a.name)));
      setNote(editing.note);
      setQty(editing.qty);
    } else {
      setSize(product.sizes?.[0]?.name ?? 'Regular');
      setExcluded(new Set());
      setAddons(new Set());
      setNote('');
      setQty(1);
    }
  }, [open, product?.id, editing != null]);

  if (!product) return null;

  const sizeDelta = product.sizes?.find((s) => s.name === size)?.priceDelta ?? 0;
  const addonsTotal = (product.availableAddons ?? [])
    .filter((a) => addons.has(a.name))
    .reduce((s, a) => s + a.priceCents, 0);
  const unitPriceCents = product.basePriceCents + sizeDelta + addonsTotal;
  const total = unitPriceCents * qty;

  const toggleExcluded = (ing: string) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(ing)) next.delete(ing);
      else next.add(ing);
      return next;
    });
  };
  const toggleAddon = (a: string) => {
    setAddons((prev) => {
      const next = new Set(prev);
      if (next.has(a)) next.delete(a);
      else next.add(a);
      return next;
    });
  };

  const handleAdd = () => {
    onAdd({
      size,
      unitPriceCents,
      excludedIngredients: Array.from(excluded),
      addedAddons: (product.availableAddons ?? []).filter((a) => addons.has(a.name)),
      note: note.trim(),
      qty,
    });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} size="lg" hideClose>
      {/* Escape Modal's default p-6 so the image hero can go edge-to-edge */}
      <div className="-m-6">
        {/* Header image — 16:9 banner. Same aspect whether photo or
            placeholder tile, so layout never jumps and a real photo
            won't be awkwardly stretched into a portrait slot. */}
        <div className="relative aspect-[16/9] w-full overflow-hidden rounded-t-xl">
          <ProductImage
            src={product.imageUrl}
            name={product.name}
            seed={product.id}
            bgClassName={product.bgClassName}
          />
          {/* Eyebrow + close, floated over the image */}
          <div className="absolute top-3 left-4 right-3 flex items-start justify-between">
            <span className="inline-flex items-center h-7 px-2.5 rounded-md bg-black/40 backdrop-blur-sm text-12 font-extrabold uppercase tracking-wider text-[#FFFFFF]">
              {product.category}
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-9 h-9 inline-flex items-center justify-center rounded-md bg-black/40 backdrop-blur-sm text-[#FFFFFF] hover:bg-black/55 transition-colors"
            >
              <IconX size={18} stroke={2.25} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pt-5 pb-5">
          <h2 className="text-22 font-extrabold text-heading leading-tight">{product.name}</h2>
          {product.description && (
            <p className="text-14 font-medium text-gray-700 mt-1.5 leading-snug">{product.description}</p>
          )}

          {/* Sizes */}
          {product.sizes && product.sizes.length > 0 && (
            <div className="mt-5">
              <h4 className="text-14 font-extrabold text-heading mb-2.5">Size</h4>
              <div className="grid grid-cols-3 gap-2">
                {product.sizes.map((s) => {
                  const active = size === s.name;
                  return (
                    <button
                      key={s.name}
                      type="button"
                      onClick={() => setSize(s.name)}
                      className={[
                        'flex flex-col items-center justify-center py-3 rounded-lg border-2 transition-all',
                        active
                          ? 'border-primary bg-primary-soft text-primary shadow-sm'
                          : 'border-border bg-surface text-heading hover:border-gray-400',
                      ].join(' ')}
                    >
                      <span className="text-15 font-bold">{s.name}</span>
                      <span className={[
                        'text-13 font-semibold tabular-nums mt-0.5',
                        active ? 'text-primary' : 'text-gray-700',
                      ].join(' ')}>
                        {s.priceDelta === 0
                          ? fmtR(product.basePriceCents)
                          : `+${fmtR(s.priceDelta)}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Default ingredients */}
          {product.defaultIngredients && product.defaultIngredients.length > 0 && (
            <div className="mt-5">
              <div className="flex items-baseline justify-between mb-2.5">
                <h4 className="text-14 font-extrabold text-heading">Ingredients</h4>
                <p className="text-12 font-semibold text-gray-600 uppercase tracking-wide">Tap to remove</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.defaultIngredients.map((ing) => {
                  const isExcluded = excluded.has(ing);
                  return (
                    <button
                      key={ing}
                      type="button"
                      onClick={() => toggleExcluded(ing)}
                      className={[
                        'inline-flex items-center gap-1.5 px-3 h-10 rounded-md text-14 font-semibold border-2 transition-colors',
                        isExcluded
                          ? 'bg-danger-soft border-danger/40 text-danger line-through'
                          : 'bg-surface border-border text-heading hover:border-gray-400',
                      ].join(' ')}
                    >
                      {isExcluded
                        ? <IconX size={14} stroke={2.75} />
                        : <IconCheck size={14} stroke={2.75} />}
                      {ing}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add-ons */}
          {product.availableAddons && product.availableAddons.length > 0 && (
            <div className="mt-5">
              <div className="flex items-baseline justify-between mb-2.5">
                <h4 className="text-14 font-extrabold text-heading">Add-ons</h4>
                <p className="text-12 font-semibold text-gray-600 uppercase tracking-wide">Tap to add</p>
              </div>
              <div className="space-y-2">
                {product.availableAddons.map((a) => {
                  const isAdded = addons.has(a.name);
                  return (
                    <button
                      key={a.name}
                      type="button"
                      onClick={() => toggleAddon(a.name)}
                      className={[
                        'w-full flex items-center justify-between px-3 h-12 rounded-md border-2 transition-colors text-left',
                        isAdded
                          ? 'bg-success-soft border-success/40'
                          : 'bg-surface border-border hover:border-gray-400',
                      ].join(' ')}
                    >
                      <div className="flex items-center gap-3">
                        <span className={[
                          'w-5 h-5 rounded-md inline-flex items-center justify-center transition-colors flex-shrink-0',
                          isAdded ? 'bg-success text-[#FFFFFF]' : 'border-2 border-border bg-surface',
                        ].join(' ')}>
                          {isAdded && <IconCheck size={13} stroke={3} />}
                        </span>
                        <span className="text-14 font-semibold text-heading">{a.name}</span>
                      </div>
                      <span className={[
                        'text-14 font-extrabold tabular-nums',
                        a.priceCents === 0 ? 'text-gray-700' : 'text-success',
                      ].join(' ')}>
                        {a.priceCents === 0 ? 'Free' : `+${fmtR(a.priceCents)}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Note */}
          <div className="mt-5">
            <h4 className="text-14 font-extrabold text-heading mb-2.5">Special note</h4>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Extra hot, no sugar"
              className="w-full h-12 px-4 rounded-md border-2 border-border bg-surface text-14 font-medium text-heading placeholder-gray-500 outline-none focus:border-primary transition-colors"
            />
          </div>
        </div>

        {/* Sticky-feeling footer: Qty + Add */}
        <div className="px-6 py-4 border-t border-border flex items-center gap-3 bg-light rounded-b-xl">
          <div className="inline-flex items-center bg-surface rounded-md border-2 border-border overflow-hidden">
            <button
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              disabled={qty <= 1}
              className="w-10 h-12 inline-flex items-center justify-center text-gray-700 hover:bg-gray-100 disabled:opacity-30 transition-colors"
              aria-label="Decrease quantity"
            >
              <IconMinus size={16} stroke={2.5} />
            </button>
            <span className="px-3 text-16 font-extrabold tabular-nums text-heading min-w-[32px] text-center">
              {qty}
            </span>
            <button
              onClick={() => setQty((q) => q + 1)}
              className="w-10 h-12 inline-flex items-center justify-center text-primary hover:bg-primary-soft transition-colors"
              aria-label="Increase quantity"
            >
              <IconPlus size={16} stroke={2.5} />
            </button>
          </div>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleAdd}
            leadingIcon={<IconPlus size={18} stroke={2.75} />}
          >
            {isEditing ? 'Update' : 'Add'} · {fmtR(total)}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
